import type { CSSProperties, FC, ReactNode } from 'react'
import type { Guide } from '@/constants/playbook'

import { useState } from 'react'
import { Check, Clock3, Download, Flame } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { NewsletterForm } from '@/components/kit/newsletter-form'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { Tilt } from '@/components/kit/tilt'
import { guides } from '@/constants/playbook'

type Level = Guide['level']

const levels: Array<'All' | Level> = ['All', 'Easy', 'Medium', 'Spicy']

const stolen = [
    { slug: 'the-3-second-hook', count: 4812 },
    { slug: 'the-no-list', count: 3270 },
    { slug: 'a-month-in-an-afternoon', count: 2941 }
]

const toText = (guide: Guide): string => {
    return [
        `UPDRAFT PLAYBOOK · CARD ${guide.number}`,
        guide.title.toUpperCase(),
        guide.hook,
        '',
        `Time: ${guide.minutes} minutes · Level: ${guide.level} · Rarity: ${guide.rarity}`,
        '',
        'THE STEPS',
        ...guide.steps.map((step, index) => `${index + 1}. ${step}`),
        '',
        'Catch the lift.',
        'Updraft Studio, London. hello@updraft.studio'
    ].join('\n')
}

const GuideCard: FC<{ guide: Guide, index: number }> = ({ guide, index }) => {
    const [saved, setSaved] = useState(false)
    const holo = guide.rarity === 'Holo'

    const download = () => {
        const blob = new Blob([toText(guide)], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.download = `updraft-playbook-${guide.number}-${guide.slug}.txt`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
        setSaved(true)
    }

    return (
        <Reveal
            delay={(index % 4) * 80}
            settle={[-1.5, 1, -0.6, 1.4][index % 4]}
        >
            <Tilt max={8}>
                <article
                    className='group relative flex aspect-[5/7] flex-col overflow-hidden rounded-[1.5rem] border-[6px] border-paper p-5 shadow-[0_30px_50px_-30px_rgb(30_30_30/0.6)]'
                    style={{ backgroundColor: guide.tone, color: guide.ink }}
                >
                    {holo && (
                        <span
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0 opacity-0 mix-blend-soft-light transition-opacity duration-500 group-hover:opacity-100'
                            style={{ backgroundImage: 'conic-gradient(from 120deg at var(--glare-x, 50%) var(--glare-y, 50%), #ff5c00, #ffe27a, #8fd3ff, #ff9ad5, #ff5c00)' } as CSSProperties}
                        />
                    )}

                    <div className='relative flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] opacity-80'>
                        <span>No. {guide.number}</span>
                        <span className={cn('rounded-full border border-current px-2 py-0.5', holo && 'bg-paper/90 text-ink')}>{guide.rarity}</span>
                    </div>

                    <h3 className='relative mt-6 font-heading text-[clamp(1.6rem,2.2vw,2rem)] font-bold leading-[0.98] tracking-[-0.04em]'>
                        {guide.title}
                    </h3>
                    <p className='relative mt-2 text-[14px] leading-snug opacity-80'>{guide.hook}</p>

                    <ul className='relative mt-5 flex flex-col gap-1.5 border-t border-current/20 pt-4'>
                        {guide.preview.map(item => (
                            <li
                                key={item}
                                className='flex items-start gap-2 text-[13px] leading-snug'
                            >
                                <span className='mt-[6px] size-1.5 shrink-0 rounded-full bg-current' />
                                {item}
                            </li>
                        ))}
                    </ul>

                    <div className='relative mt-auto flex items-center gap-3 pt-4 font-mono text-[10px] uppercase tracking-[0.12em] opacity-80'>
                        <span className='inline-flex items-center gap-1'>
                            <Clock3 className='size-3' />
                            {guide.minutes} min
                        </span>
                        <span className='inline-flex items-center gap-1'>
                            <Flame className='size-3' />
                            {guide.level}
                        </span>
                    </div>

                    <button
                        type='button'
                        onClick={download}
                        className='relative mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-ink text-sm font-medium text-cream transition-colors hover:bg-black'
                        style={guide.tone === '#1e1e1e' ? { backgroundColor: '#ff5c00', color: '#1e1e1e' } : undefined}
                    >
                        {saved ? <Check className='size-4' /> : <Download className='size-4' />}
                        {saved ? 'Card saved' : 'Download the card'}
                    </button>
                </article>
            </Tilt>
        </Reveal>
    )
}

const Playbook: FC = (): ReactNode => {
    const [level, setLevel] = useState<'All' | Level>('All')
    const visible = guides.filter(guide => level === 'All' || guide.level === level)

    return (
        <Layout>
            <Seo
                title='Playbook'
                description='Free mini guides from Updraft, made as collectible cards: the 3 second hook, a month of content in one afternoon, captions that get replies and more.'
            />

            <section
                aria-labelledby='playbook-title'
                className='relative overflow-hidden pb-16 pt-36 md:pb-20 md:pt-44'
            >
                <div className='relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 md:px-10 lg:grid-cols-12'>
                    <div className='lg:col-span-7'>
                        <SectionLabel>Playbook · free to steal</SectionLabel>
                        <SplitText
                            as='h1'
                            id='playbook-title'
                            lines={['Collect all', 'eight cards']}
                            italic='eight'
                            className='mt-6 text-[clamp(3.25rem,9.5vw,8.5rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                        />
                        <Reveal
                            as='p'
                            delay={250}
                            className='mt-8 max-w-lg text-xl leading-relaxed text-stone'
                        >
                            Our best habits, boiled down to one card each. Read them in a coffee break, download the steps and stick them on the wall above your desk.
                        </Reveal>
                    </div>

                    <Reveal
                        delay={300}
                        className='relative hidden h-[420px] lg:col-span-5 lg:block'
                    >
                        {guides.slice(0, 3).map((guide, index) => (
                            <div
                                key={guide.slug}
                                className='absolute left-1/2 top-1/2 flex aspect-[5/7] w-[230px] flex-col justify-between rounded-[1.25rem] border-[5px] border-paper p-4 shadow-[0_30px_50px_-30px_rgb(30_30_30/0.6)]'
                                style={{
                                    backgroundColor: guide.tone,
                                    color: guide.ink,
                                    transform: `translate(-50%, -50%) translateX(${(index - 1) * 90}px) rotate(${(index - 1) * 10}deg)`,
                                    zIndex: index === 1 ? 3 : 1
                                }}
                            >
                                <span className='font-mono text-[10px] uppercase tracking-[0.16em] opacity-80'>No. {guide.number}</span>
                                <span className='font-heading text-2xl font-bold leading-[0.98] tracking-[-0.04em]'>{guide.title}</span>
                            </div>
                        ))}
                        <span
                            className='stamp-in stamp absolute bottom-6 right-4 z-10 bg-cream text-ember'
                            data-on='true'
                            style={{ '--stamp-rotate': '-12deg', '--stamp-delay': '0.9s' } as CSSProperties}
                        >
                            Free, forever
                        </span>
                    </Reveal>
                </div>
            </section>

            <section
                aria-labelledby='library-title'
                className='mx-auto max-w-[1400px] px-5 pb-20 md:px-10 md:pb-28'
            >
                <div className='flex flex-col gap-6 border-t border-ink/15 pt-8 md:flex-row md:items-center md:justify-between'>
                    <h2
                        id='library-title'
                        className='text-[clamp(2rem,3.6vw,3rem)] font-bold tracking-[-0.04em]'
                    >
                        The <span className='font-serif font-normal italic tracking-[-0.01em]'>library</span>
                    </h2>
                    <div
                        role='group'
                        aria-label='Filter by level'
                        className='flex flex-wrap gap-2'
                    >
                        {levels.map(item => (
                            <button
                                key={item}
                                type='button'
                                aria-pressed={level === item}
                                onClick={() => setLevel(item)}
                                className={cn(
                                    'rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300',
                                    level === item ? 'border-ink bg-ink text-cream' : 'border-ink/15 bg-paper/60 text-ink hover:border-ink/40'
                                )}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>

                <div className='mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4'>
                    {visible.map((guide, index) => (
                        <GuideCard
                            key={guide.slug}
                            guide={guide}
                            index={index}
                        />
                    ))}
                </div>
            </section>

            <section
                aria-labelledby='stolen-title'
                className='bg-sand py-20 md:py-28'
            >
                <div className='mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <SectionLabel>Leaderboard</SectionLabel>
                        <SplitText
                            as='h2'
                            id='stolen-title'
                            lines={['Most stolen', 'this month']}
                            italic='stolen'
                            className='mt-5 text-[clamp(2.5rem,5.6vw,5rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />
                        <p className={cn(hand.className, 'mt-6 -rotate-2 text-2xl text-ember')}>theft is encouraged here</p>
                    </div>

                    <ol className='flex flex-col gap-3 lg:col-span-7'>
                        {stolen.map((item, index) => {
                            const guide = guides.find(entry => entry.slug === item.slug)

                            if (!guide) return null

                            return (
                                <Reveal
                                    as='li'
                                    key={item.slug}
                                    delay={index * 90}
                                >
                                    <div className='flex items-center gap-5 rounded-2xl border border-ink/10 bg-paper p-5'>
                                        <span className='font-heading text-5xl font-bold leading-none tracking-[-0.05em] text-ember'>{index + 1}</span>
                                        <div className='min-w-0 flex-1'>
                                            <p className='font-heading text-2xl font-bold leading-tight tracking-[-0.035em]'>{guide.title}</p>
                                            <p className='text-sm text-stone'>{guide.hook}</p>
                                        </div>
                                        <span className='shrink-0 font-mono text-xs text-stone'>{new Intl.NumberFormat('en-GB').format(item.count)} downloads</span>
                                    </div>
                                </Reveal>
                            )
                        })}
                    </ol>
                </div>
            </section>

            <section
                aria-labelledby='use-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='grid gap-12 lg:grid-cols-12 lg:items-center'>
                    <div className='lg:col-span-6'>
                        <SectionLabel>How to use them</SectionLabel>
                        <SplitText
                            as='h2'
                            id='use-title'
                            lines={['One card,', 'one week']}
                            italic='week'
                            className='mt-5 text-[clamp(2.5rem,5.6vw,5rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />
                        <ul className='mt-10 flex flex-col divide-y divide-ink/10 border-y border-ink/10'>
                            {[
                                'Pick one card on Monday. Not three. One.',
                                'Try the steps on two or three posts that week.',
                                'Read your numbers the next Monday with card 007.',
                                'Keep what worked. Stick the card on the wall.'
                            ].map((tip, index) => (
                                <li
                                    key={tip}
                                    className='flex items-baseline gap-5 py-4 text-lg'
                                >
                                    <span className='font-mono text-xs text-ember'>0{index + 1}</span>
                                    {tip}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <Reveal
                        delay={150}
                        settle={-1.2}
                        className='lg:col-span-5 lg:col-start-8'
                    >
                        <div className='relative rounded-[2rem] bg-ink p-8 text-cream md:p-10'>
                            <span
                                aria-hidden='true'
                                className='tape -top-3 left-10 -rotate-6'
                            />
                            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-ember'>A new card every month</p>
                            <p className='mt-4 font-heading text-[clamp(2rem,3.4vw,2.75rem)] font-bold leading-[1] tracking-[-0.04em]'>
                                Card nine is being printed.
                            </p>
                            <p className='mt-3 text-base leading-relaxed text-cream/70'>
                                Subscribers to the Tuesday Signal get each new card a week before it goes up here.
                            </p>
                            <NewsletterForm
                                source='playbook'
                                tone='dark'
                                className='mt-7'
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <FinalCta
                lines={['Rather we played', 'the cards for you?']}
                italic='you'
                body='The playbook is how we work every day. If you would rather hand it over, we will run it on your brand and send a free forecast first.'
            />
        </Layout>
    )
}

export default Playbook
